import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Layout & UI
import { PageDetailLayout } from '@/shared/layout';
import { Button, Card, FeaturePlaceholder, KPICard, Avatar, StatusBadge } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useToast } from '@/components/ui/Toast';

// Hooks & Auth
import { useProjectZoneDetail } from './hooks/useZones';
import { useAuth } from '@/core/auth/AuthContext';

// Utils
import { getZoneStatus, getZoneType } from '@/utils/status';
import { cn } from '@/utils/cn';

/**
 * ZoneDetailPage - Comprehensive view of a single landscape zone
 * Optimized for Landscape High-end Landscape Management
 */
export default function ZoneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();

  const { zone: item, isLoading, updateZone } = useProjectZoneDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 sm:text-sm text-xs">Đang tải chi tiết khu vực...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin khu vực</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateZone({ status_code: newStatus as any });
      const statusCfg = getZoneStatus(newStatus);
      toast.success('Đã chuyển trạng thái khu vực sang ' + statusCfg.label);
    } catch (error: any) {
      toast.error('Lỗi cập nhật: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'boq', label: 'Khối lượng / BOQ' },
    { value: 'work_orders', label: 'Lệnh việc (Work Orders)' },
    { value: 'progress', label: 'Tiến độ' },
    { value: 'qa', label: 'QA / Nghiệm thu' },
    { value: 'issues', label: 'Sự cố & NCR' },
    { value: 'documents', label: 'Bản vẽ / Spec' },
  ];

  const typeCfg = getZoneType(item.zone_type);

  return (
    <PageDetailLayout
      title={item.zone_name}
      description={
        <div className="flex items-center gap-3 mt-1.5">
          <StatusBadge status={item.status_code} type="zone" size="sm" />
          <span className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest">#{item.zone_code}</span>
          <span className="text-slate-200">|</span>
          <div className="flex items-center gap-1.5 sm:text-sm text-xs font-normal text-slate-500">
            <i className="ri-building-line text-slate-300" />
            {item.project?.name}
          </div>
        </div>
      }
      breadcrumbs={[
        { label: 'Khu vực dự án', path: '/projects/zones' },
        { label: item.zone_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'qa_pending' && (
            <AuthButton
              module="projects"
              action="update"
              variant="success"
              icon="ri-check-line"
              onClick={() => handleStatusChange('accepted')}
            >
              Nghiệm thu Khu vực
            </AuthButton>
          )}
          <AuthButton module="projects" action="update" variant="outline" icon="ri-edit-line">
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard
                  title="Diện tích quản lý"
                  value={item.area_m2?.toLocaleString() || '0'}
                  icon="ri-ruler-2-line"
                  variant="primary"
                  suffix=" m²"
                />
                <KPICard
                  title="Sức khỏe khu vực"
                  value={item.health_score}
                  icon="ri-heart-pulse-line"
                  variant="success"
                  suffix="%"
                />
                <KPICard
                  title="Độ khó thi công"
                  value={item.difficulty_score}
                  icon="ri-speed-line"
                  variant="warning"
                  suffix="/100"
                />
              </div>

              <Card className="p-6 border-none shadow-sm overflow-hidden relative">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                  <i className="ri-leaf-line text-8xl" />
                </div>

                <h3 className="font-normal text-slate-900 mb-6 flex items-center gap-2 sm:text-sm text-xs tracking-widest">
                  <i className="ri-leaf-line text-brand-500" />
                  Thông số Cảnh quan chi tiết
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Diện tích cỏ</p>
                    <p className="sm:text-sm text-xs font-black text-slate-800">{(item.grass_area_m2 || 0).toLocaleString()} m²</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Diện tích cây bụi</p>
                    <p className="sm:text-sm text-xs font-black text-slate-800">{(item.shrub_area_m2 || 0).toLocaleString()} m²</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Số cây bóng mát</p>
                    <p className="sm:text-sm text-xs font-black text-slate-800">{item.tree_count || 0} cây</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Điểm vòi tưới</p>
                    <p className="sm:text-sm text-xs font-black text-slate-800">{item.irrigation_point_count || 0} điểm</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Chiều dài hạ tầng (Hardscape)</p>
                    <p className="sm:text-sm text-xs font-black text-slate-800">{(item.hardscape_edge_length_m || 0).toLocaleString()} m</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 tracking-tighter">Mức độ bảo trì</p>
                    <div className="flex items-center gap-1.5">
                      <span className="sm:text-sm text-xs font-black text-brand-600">LEVEL {item.maintenance_level}</span>
                      <i className="ri-medal-line text-brand-400" />
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-slate-100">
                  <label className="block text-[10px] font-black text-slate-400 tracking-widest mb-3">Mô tả & Ghi chú tiếp cận</label>
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100/50">
                    <p className="sm:text-sm text-xs text-slate-600 leading-relaxed italic">
                      "{item.description || 'Chưa có thông tin mô tả chi tiết cho khu vực này.'}"
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-none shadow-sm">
                <h3 className="font-normal text-slate-900 mb-4 flex items-center gap-2 sm:text-sm text-xs tracking-widest">
                  <i className="ri-map-pin-line text-slate-400" />
                  Vị trí & Phân cấp
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-slate-50">
                    <span className="sm:text-sm text-xs font-normal text-slate-500">Phân loại khu vực</span>
                    <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-lg border border-slate-100">
                      <i className={cn(typeCfg.icon, "text-slate-400 sm:text-sm text-xs")} />
                      <span className="sm:text-sm text-xs font-normal text-slate-700">{typeCfg.label}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="sm:text-sm text-xs font-normal text-slate-500">Khu vực cha (Parent)</span>
                    <span className={cn(
                      "text-sm font-bold",
                      item.parent_zone?.id ? "text-os-primary cursor-pointer hover:underline" : "text-slate-400"
                    )}>
                      {item.parent_zone?.zone_name || 'Không có'}
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white overflow-hidden relative border-none shadow-xl">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <i className="ri-pulse-line text-6xl" />
                </div>
                <h4 className="font-bold mb-6 flex items-center gap-2 text-brand-400 text-[10px] tracking-widest">
                  <i className="ri-pulse-line" />
                  Hoạt động Hiện trường
                </h4>
                <div className="space-y-4 relative z-10">
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                    <p className="text-[9px] text-slate-400 font-bold tracking-widest mb-1.5">Trạng thái thi công</p>
                    <p className="sm:text-sm text-xs font-normal text-emerald-400">Dữ liệu từ BOQ đang được đồng bộ...</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                    <p className="text-[9px] text-slate-400 font-bold tracking-widest mb-1.5">Sự cố tồn đọng</p>
                    <p className="sm:text-sm text-xs font-normal text-slate-300">Không có sự cố nghiêm trọng</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-none shadow-sm">
                <h4 className="font-bold text-slate-900 mb-6 flex items-center gap-2 text-[10px] tracking-widest">
                  <i className="ri-team-line text-slate-400" />
                  Đội ngũ phụ trách
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <Avatar
                      size="sm"
                      src={item.manager?.avatar_path}
                      name={item.manager?.full_name}
                      className="ring-2 ring-white shadow-sm"
                    />
                    <div>
                      <p className="text-[9px] font-bold text-slate-400 tracking-tighter">Phụ trách chính (Foreman)</p>
                      <p className="sm:text-sm text-xs font-black text-slate-800">{item.manager?.full_name || 'Chưa gán'}</p>
                    </div>
                  </div>
                </div>
                <AuthButton
                  module="projects"
                  action="update"
                  variant="ghost"
                  size="sm"
                  className="w-full mt-6 text-brand-600 text-[10px] font-black tracking-widest bg-brand-50/50 hover:bg-brand-50 rounded-xl py-2.5 transition-all"
                >
                  Giao đội phụ trách
                </AuthButton>
              </Card>
            </div>
          </div>
        )}

        {activeTab !== 'overview' && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được liên kết từ các phân hệ BOQ, Quản lý thi công, QA Nghiệm thu và Quản lý sự cố."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
